import { useEffect, useState } from "react"

import {
    describeCars,
    fetchAllCars,
    fetchCarById,
    fetchCars,
    fetchSimilarCars,
} from "../services/api"
import { enrichVehicle } from "../utils/vehicleDetail"
import { useAosOnContent, useAsyncData } from "./useAsyncData"

export const useCarPreview = (pageSize = 3) => {
    const { data: description, loading: descriptionLoading } = useAsyncData(
        () => describeCars().then(({ data }) => data),
        [pageSize]
    )

    const { data: cars, loading: carsLoading } = useAsyncData(
        () => fetchCars(0, pageSize).then(({ data }) => data.cars || []),
        [pageSize]
    )

    useAosOnContent(cars)

    return {
        cars: cars || [],
        totalCars: description?.cars ?? 0,
        loading: descriptionLoading || carsLoading,
    }
}

export const useFleetCount = () => {
    const { data, loading } = useAsyncData(
        () => describeCars().then(({ data }) => data.cars ?? 0),
        []
    )

    return { count: data ?? 0, loading }
}

export const useAllCars = () => {
    const { data: cars, loading } = useAsyncData(
        () => fetchAllCars().then(({ data }) => data.cars || []),
        []
    )

    useAosOnContent(cars)

    return { cars: cars || [], loading }
}

export const useCarDetail = (vehicleId) => {
    const { data: vehicle, loading, error } = useAsyncData(
        () => fetchCarById(vehicleId).then(({ data }) => enrichVehicle(data)),
        [vehicleId]
    )

    return { vehicle, loading, error }
}

export const useSimilarCars = (vehicleId, limit = 8) => {
    const { data: cars, loading } = useAsyncData(
        () =>
            vehicleId
                ? fetchSimilarCars(vehicleId, limit).then(({ data }) => data.cars || [])
                : Promise.resolve([]),
        [vehicleId, limit]
    )

    return { cars: cars || [], loading }
}

export const useClientPagination = (items, pageSize, resetKey) => {
    const [currentPage, setCurrentPage] = useState(1)
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize))

    const paginatedItems = items.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    useEffect(() => {
        setCurrentPage(1)
    }, [resetKey, pageSize])

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    return {
        paginatedItems,
        currentPage,
        setCurrentPage,
        totalPages,
    }
}
